/**
 * LeadsFilter Component
 * Filtering and searching leads for advisor panel
 */

import { useState, useEffect } from "react";
import { Search, Filter, X, ChevronDown } from "lucide-react";
import { Button } from "@/shared/components/ui";
import type { CreditType } from "@/shared/types";

export interface LeadsFilterState {
  search: string;
  creditType: CreditType | "all";
  amountMin?: number;
  amountMax?: number;
  dateRange: "all" | "7d" | "30d" | "90d";
  sortBy: "createdAt" | "amount";
  sortOrder: "asc" | "desc";
}

const FILTER_STORAGE_KEY = "advisor-panel:leads-filter";

const DEFAULT_FILTER: LeadsFilterState = {
  search: "",
  creditType: "all",
  amountMin: undefined,
  amountMax: undefined,
  dateRange: "all",
  sortBy: "createdAt",
  sortOrder: "desc",
};

const CREDIT_TYPE_OPTIONS: { value: CreditType | "all"; label: string }[] = [
  { value: "all", label: "Wszystkie typy" },
  { value: "hipoteczny", label: "Hipoteczny" },
  { value: "gotowkowy", label: "Gotówkowy" },
  { value: "konsolidacyjny", label: "Konsolidacyjny" },
];

const DATE_RANGE_OPTIONS: { value: LeadsFilterState["dateRange"]; label: string }[] = [
  { value: "all", label: "Wszystkie" },
  { value: "7d", label: "Ostatnie 7 dni" },
  { value: "30d", label: "Ostatnie 30 dni" },
  { value: "90d", label: "Ostatnie 90 dni" },
];

const SORT_OPTIONS: { value: LeadsFilterState["sortBy"]; label: string }[] = [
  { value: "createdAt", label: "Data" },
  { value: "amount", label: "Kwota" },
];

interface LeadsFilterProps {
  value: LeadsFilterState;
  onChange: (filter: LeadsFilterState) => void;
  totalCount: number;
  filteredCount: number;
}

export function LeadsFilter({
  value,
  onChange,
  totalCount,
  filteredCount,
}: LeadsFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasActiveFilters =
    value.creditType !== "all" ||
    value.dateRange !== "all" ||
    value.amountMin !== undefined ||
    value.amountMax !== undefined;

  const handleReset = () => {
    onChange(DEFAULT_FILTER);
    setIsExpanded(false);
  };

  const toggleSort = () => {
    onChange({
      ...value,
      sortOrder: value.sortOrder === "asc" ? "desc" : "asc",
    });
  };

  return (
    <div className="space-y-3">
      {/* Search and quick filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Szukaj po nazwie klienta..."
            value={value.search}
            onChange={(e) => onChange({ ...value, search: e.target.value })}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {value.search && (
            <button
              onClick={() => onChange({ ...value, search: "" })}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <Button
          variant={hasActiveFilters ? "primary" : "outline"}
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          Filtry
          {hasActiveFilters && (
            <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
              aktywne
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </Button>
      </div>

      {/* Expanded filters */}
      {isExpanded && (
        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Credit Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ kredytu
              </label>
              <select
                value={value.creditType}
                onChange={(e) =>
                  onChange({
                    ...value,
                    creditType: e.target.value as CreditType | "all",
                  })
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              >
                {CREDIT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Okres
              </label>
              <select
                value={value.dateRange}
                onChange={(e) =>
                  onChange({
                    ...value,
                    dateRange: e.target.value as LeadsFilterState["dateRange"],
                  })
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              >
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Amount Min */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kwota od (zł)
              </label>
              <input
                type="number"
                placeholder="0"
                value={value.amountMin ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    amountMin: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Amount Max */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kwota do (zł)
              </label>
              <input
                type="number"
                placeholder="∞"
                value={value.amountMax ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    amountMax: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <Button variant="ghost" size="sm" onClick={handleReset}>
              Resetuj filtry
            </Button>
          </div>
        </div>
      )}

      {/* Sort and count */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <span className="text-gray-600">Sortuj:</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                if (value.sortBy === opt.value) {
                  toggleSort();
                } else {
                  onChange({ ...value, sortBy: opt.value, sortOrder: "desc" });
                }
              }}
              className={`flex items-center gap-1 px-2 py-1 rounded ${
                value.sortBy === opt.value
                  ? "bg-primary-100 text-primary-700 font-medium"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {opt.label}
              {value.sortBy === opt.value && (
                <span className="text-xs">
                  {value.sortOrder === "desc" ? "↓" : "↑"}
                </span>
              )}
            </button>
          ))}
        </div>

        <span className="text-gray-500">
          {filteredCount === totalCount
            ? `${totalCount} leadów`
            : `${filteredCount} z ${totalCount} leadów`}
        </span>
      </div>
    </div>
  );
}

// Hook for filter persistence
export function useLeadsFilter() {
  const [filter, setFilter] = useState<LeadsFilterState>(() => {
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_FILTER, ...JSON.parse(stored) };
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_FILTER;
  });

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
  }, [filter]);

  return [filter, setFilter] as const;
}

// Filter function
export function filterLeads<T extends {
  name?: string;
  creditType?: string;
  amount?: number;
  createdAt?: string;
}>(leads: T[], filter: LeadsFilterState): T[] {
  let result = [...leads];

  // Search by name
  if (filter.search.trim()) {
    const searchLower = filter.search.toLowerCase();
    result = result.filter((lead) =>
      lead.name?.toLowerCase().includes(searchLower)
    );
  }

  // Filter by credit type
  if (filter.creditType !== "all") {
    result = result.filter((lead) => lead.creditType === filter.creditType);
  }

  // Filter by amount range
  if (filter.amountMin !== undefined) {
    result = result.filter((lead) => (lead.amount ?? 0) >= filter.amountMin!);
  }
  if (filter.amountMax !== undefined) {
    result = result.filter((lead) => (lead.amount ?? 0) <= filter.amountMax!);
  }

  // Filter by date range
  if (filter.dateRange !== "all") {
    const now = new Date();
    const days = filter.dateRange === "7d" ? 7 : filter.dateRange === "30d" ? 30 : 90;
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    result = result.filter((lead) => {
      const createdAt = lead.createdAt ? new Date(lead.createdAt) : new Date(0);
      return createdAt >= cutoff;
    });
  }

  // Sort
  result.sort((a, b) => {
    let comparison = 0;

    if (filter.sortBy === "createdAt") {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      comparison = dateA - dateB;
    } else if (filter.sortBy === "amount") {
      comparison = (a.amount ?? 0) - (b.amount ?? 0);
    }

    return filter.sortOrder === "asc" ? comparison : -comparison;
  });

  return result;
}

export { DEFAULT_FILTER };
